import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

export interface TutorialStep {
  id: string;
  cardType: string;
  title: string;
  description: string;
  details: string[];
  codeExample?: string;
}

const tutorialSteps: TutorialStep[] = [
  {
    id: 'start',
    cardType: 'start',
    title: 'Card Início',
    description:
      'Todo fluxo de bot começa aqui. Este card marca o ponto de entrada do seu bot.',
    details: [
      'Conecte este card ao primeiro card de ação do seu fluxo',
      'Apenas um card de início é permitido por bot',
    ],
  },
  {
    id: 'actions',
    cardType: 'msg_actions',
    title: 'Card Ações (Mensageria)',
    description:
      'Executa ações como enviar mensagens, arquivos, transferir para fila, etc.',
    details: [
      '**Enviar Mensagem**: Envia mensagem com suporte a variáveis (**@@VAR@@**)',
      '**Enviar Arquivo**: Envia arquivos de mídia',
      '**Enviar para Fila**: Transfere para uma fila de atendimento',
      '**Enviar para Agente**: Transfere para um agente específico',
      '**Finalizar Atendimento**: Encerra a conversa',
      '**Vincular Tag**: Vincula uma tag ao atendimento',
      '**Salvar Contato**: Salva o contato em um grupo',
      '**Salvar no Mailing**: Salva em um mailing com status',
    ],
    codeExample: `// Usando variáveis na mensagem:
Olá @@NOME@@, bem-vindo!

// Exibindo lista formatada:
Escolha uma opção:\\n@@LISTA@@

// Acessando subpropriedades:
Produto: {{ITEM.nome}} - R$ {{ITEM.preco}}

// @@VAR@@ = valor inteiro
// {{VAR.prop}} = subpropriedade`,
  },
  {
    id: 'api',
    cardType: 'api',
    title: 'Card API',
    description:
      'Faz requisições HTTP para APIs externas e salva os dados em variáveis.',
    details: [
      'Suporta **GET**, **POST**, **PUT**, **DELETE**, **PATCH**',
      '**Headers** e **Query Params** personalizáveis',
      '**Body** em JSON ou texto com variáveis',
      '**Caminho da Lista**: Acessa dados aninhados (ex: data.pessoas)',
      '**Tratar Lista**: Transforma array em lista numerada',
      '**Salvar Seleção**: Captura item selecionado',
      '⚡ **Lista com 1 item**: Variável de seleção é preenchida automaticamente! Use Condição "existe" antes de exibir a lista para pular quando desnecessário',
    ],
    codeExample: `// API retorna:
{ data: { pessoas: [
  { id: 1, nome: João },
  { id: 2, nome: Maria }
]}}

// Configuração:
Caminho da Lista: data.pessoas
Tratar Lista: ativado
Formato: \${id} - \${nome}

// Resultado em @@LISTA@@:
1 - João
2 - Maria

// Array simples (+ Valor Puro):
{ anos: ["2024", "2025"] }
Formato: \${VALOR_ARRAY}
// Resultado: 1 - 2024, 2 - 2025`,
  },
  {
    id: 'condition',
    cardType: 'condition',
    title: 'Card Condição',
    description: 'Avalia condições e direciona o fluxo para Sim ou Não.',
    details: [
      '**Variáveis**: Compare valores de variáveis',
      '**Propriedade**: Acesse campos de objetos (data.nome)',
      '**Validar Retorno Lista**: Valida se resposta está na lista',
      '**Grupo de Horário**: Valida horário de funcionamento',
      '**Tags**: Verifica se atendimento possui uma tag',
      'Operadores: igual, contém, começa com, maior, menor, **existe**',
      '⚡ Use "**existe**" para verificar se variável de lista foi preenchida automaticamente (1 item)',
    ],
    codeExample: `// Validar Retorno Lista:
// Valida se resposta corresponde à lista

// Se válido (saída Sim):
@@LISTA_SELECIONADA@@ = item escolhido
{{LISTA_SELECIONADA.id}} = subpropriedade

// Áudio é normalizado:
opção um → 1
segundo → 2`,
  },
  {
    id: 'menu',
    cardType: 'menu',
    title: 'Card Menu',
    description: 'Apresenta opções ao cliente e aguarda a resposta.',
    details: [
      '**Opções numéricas**: Cliente digita 1, 2, 3...',
      '**Botões**: Botões interativos do WhatsApp (máx 3)',
      '**Botões dinâmicos**: Gerados de uma lista (⚠️ **API Oficial**)',
      '  → **Salvar em**: Variável para armazenar seleção',
      '  → **Propriedade**: Campo específico a salvar (id, nome...)',
      '  → **+ Valor Puro**: Para arrays simples use **${VALOR_ARRAY}**',
      '**STT**: Transcreve áudio do cliente para texto',
      '**TTS**: Converte mensagem em áudio para o cliente',
      '**Timeout**: Define tempo limite para resposta',
      '**Tentativas inválidas**: Limite de erros',
    ],
    codeExample: `// Mensagem com opções:
Escolha uma opção:
*1* - Suporte
*2* - Financeiro
*3* - Cancelar

// Com TTS: bot lê a mensagem
// Com STT: cliente responde por áudio
// opção um → 1`,
  },
  {
    id: 'wait',
    cardType: 'wait_for_action',
    title: 'Card Aguardar',
    description:
      'Pausa o fluxo e aguarda ações configuráveis com múltiplas condições.',
    details: [
      '**Esperar Resposta**: Aguarda resposta do cliente pelo tempo configurado, com saídas Sim/Não',
      '**Inatividade do Cliente**: Monitora se o cliente respondeu após o agente enviar mensagem. Permite enviar lembretes automáticos',
      '**Inatividade do Agente**: Verifica se o agente está inativo após o cliente enviar mensagem',
      '**Tempo de Espera na Fila**: Monitora quanto tempo o cliente aguarda sem agente atribuído',
      'Cada condição possui sua própria saída para conectar a diferentes fluxos',
    ],
    codeExample: `// Condições disponíveis:
Esperar Resposta → Saída Sim / Não
Inatividade do Cliente → Saída própria
Inatividade do Agente → Saída própria
Tempo de Espera na Fila → Saída própria

// Cada card pode ter múltiplas condições
// configuradas simultaneamente`,
  },
  {
    id: 'data_input',
    cardType: 'data_input',
    title: 'Card Entrada de Dados',
    description: 'Captura e valida entrada do cliente com regras específicas.',
    details: [
      'Valida formato da resposta (CPF, email, telefone)',
      'Múltiplas condições de validação',
      'Saída para tentativa inválida',
      'Suporta operadores: contém, começa com, igual',
    ],
    codeExample: `// Validar CPF:
compareBy: equals
value: CPF válido

// Validar email:
compareBy: contains
value: @

// Cada resposta direciona
// para um card diferente`,
  },
  {
    id: 'ia',
    cardType: 'ia',
    title: 'Card IA (Inteligência Artificial)',
    description:
      'Utiliza IA para processar mensagens e gerar respostas automáticas.',
    details: [
      '**Token de IA**: OpenAI, Google, AWS',
      '**Prompt**: Instruções para a IA',
      '**STT/TTS**: Suporta interação por voz',
      '**Tempo de Inatividade**: Tempo máximo sem resposta',
      '**Máximo de Interações**: Limite de mensagens',
      '**Respostas**: Palavras específicas direcionam fluxo',
    ],
    codeExample: `// Configuração:
token: OpenAI API Key
prompt: Você é um assistente...

// Saídas especiais:
FINALIZAR → Card de finalização
TRANSFERIR → Card de transferência

// disabledInteraction: true
// IA responde sem esperar cliente`,
  },
  {
    id: 'script',
    cardType: 'script',
    title: 'Card Script',
    description:
      'Executa código JavaScript personalizado para lógica avançada.',
    details: [
      'Código JavaScript em sandbox seguro',
      'Acesso às variáveis do atendimento',
      'Retorno salvo em variável',
      'Útil para cálculos e formatações',
    ],
    codeExample: `// Código do script:
const valor = parseFloat(@@VALOR@@);
const desconto = valor * 0.1;
return valor - desconto;

// returnVariable: VALOR_FINAL
// Resultado salvo em @@VALOR_FINAL@@`,
  },
  {
    id: 'ura',
    cardType: 'ura',
    title: 'Card URA (Telefonia)',
    description: 'Menu de voz para atendimento telefônico com DTMF.',
    details: [
      'Reproduz **áudios** pré-gravados',
      'Captura dígitos **DTMF** (teclas do telefone)',
      '**Timeout** para resposta',
      '**Repetições** se não responder',
      'Saídas: **TIMEOUT**, **INVALID**, dígitos',
    ],
    codeExample: `// Configuração:
audios: [bemvindo.wav, menu.wav]
timeForResponse: 10 // segundos
numberOfRepetitions: 2

// Respostas DTMF:
1 → Card Suporte
2 → Card Financeiro
TIMEOUT → Repetir
INVALID → Mensagem erro`,
  },
  {
    id: 'ura_actions',
    cardType: 'ura_actions',
    title: 'Card Ações URA (Telefonia)',
    description: 'Ações específicas para fluxos de telefonia.',
    details: [
      '**Transferir Ramal**: Transfere para ramal interno',
      '**Transferir Tronco**: Transfere para número externo',
      '**Enviar para Fila**: Envia para fila de telefonia',
      '**Reproduzir Áudio**: Reproduz arquivo de áudio',
      '**Ler Dados**: Captura dígitos e salva em variável',
      '**Executar AGI**: Executa script AGI no Asterisk',
      '**Encerrar Ligação**: Desliga a chamada',
    ],
    codeExample: `// Transferir para ramal:
callToRamal: {
  ramal: 1001,
  dialingLimit: 30
}

// Ler dados:
readData: {
  variable: CPF,
  maxDigits: 11,
  audio: digite_cpf.wav
}`,
  },
  {
    id: 'flow',
    cardType: 'flow',
    title: 'Fluxo Completo - Listas',
    description:
      'Exemplo de como implementar um menu com lista dinâmica da API.',
    details: [
      '1. **Card API**: Busca dados e cria variável **LISTA**',
      '2. **Card Ações**: Envia mensagem com **@@LISTA@@**',
      '3. **Card Aguardar**: Espera resposta do cliente',
      '4. **Card Condição**: **Validar Retorno Lista** para validar',
      '5. Usa **@@LISTA_SELECIONADA@@** no próximo card',
    ],
    codeExample: `// Fluxo:
[API] → [Ações: @@LISTA@@] → [Aguardar]
    ↓
[Condição: Validar Retorno Lista]
    ↓ Sim
[Ações: Escolheu {{LISTA_SELECIONADA.nome}}]

// Áudio normalizado:
opção três → 3
o segundo → 2`,
  },
];

interface TutorialContextType {
  isOpen: boolean;
  currentStepIndex: number;
  currentStep: TutorialStep | null;
  totalSteps: number;
  allSteps: TutorialStep[];
  openTutorial: () => void;
  closeTutorial: () => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (index: number) => void;
}

const BotTutorialContext = createContext<TutorialContextType | null>(null);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = tutorialSteps[currentStepIndex] || null;
  const totalSteps = tutorialSteps.length;

  const openTutorial = useCallback(() => {
    setIsOpen(true);
    setCurrentStepIndex(0);
  }, []);

  const closeTutorial = useCallback(() => {
    setIsOpen(false);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex((prev) => prev + 1);
    } else {
      closeTutorial();
    }
  }, [currentStepIndex, totalSteps, closeTutorial]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSteps) {
        setCurrentStepIndex(index);
      }
    },
    [totalSteps],
  );

  return (
    <BotTutorialContext.Provider
      value={{
        isOpen,
        currentStepIndex,
        currentStep,
        totalSteps,
        allSteps: tutorialSteps,
        openTutorial,
        closeTutorial,
        nextStep,
        previousStep,
        goToStep,
      }}
    >
      {children}
    </BotTutorialContext.Provider>
  );
}

export function useBotTutorial() {
  const context = useContext(BotTutorialContext);
  if (!context) {
    throw new Error('useBotTutorial must be used within a TutorialProvider');
  }
  return context;
}
